import axios from 'axios';

export const ESPN_SEASON = 2025;
const SEASON_TYPE = 2;

// ── Static snapshot mode ──────────────────────────────────────────────────────
// Data is pre-fetched weekly (Tuesday mornings) by scripts/fetch-espn-snapshot.js
// and written to public/data/. This eliminates live ESPN API calls at runtime,
// which caused dropped players and flaky responses on every page load.
// Falls back to live ESPN calls if static files are unavailable.
const STATIC_PLAYERS_URL   = `/data/players_${ESPN_SEASON}.json`;
const STATIC_OFF_STATS_URL = `/data/player_stats_${ESPN_SEASON}.json`;
const STATIC_DEF_STATS_URL = `/data/player_def_stats_${ESPN_SEASON}.json`;

async function loadStaticJSON(url) {
  try {
    const res = await axios.get(url);
    return res.data;
  } catch {
    return null;
  }
}

export const POS_MAP = {
  QB:'QB', RB:'RB', FB:'RB', WR:'WR', TE:'TE',
  T:'T', OT:'T', LT:'T', RT:'T',
  G:'G', OG:'G', LG:'G', RG:'G', C:'C',
  DE:'DE', DT:'DT', NT:'DT',
  LB:'LB', ILB:'LB', OLB:'LB', MLB:'LB',
  CB:'CB', DB:'CB',
  S:'S', SS:'S', FS:'S',
  PK:'K', K:'K', P:'P',
};

export const ALL_TEAM_IDS = [
  2,15,17,20, 33,4,5,23, 34,11,30,10, 7,12,13,24,
  6,19,21,28, 3,8,9,16, 1,29,18,27, 22,14,26,25,
];

export const athIdFromRef = ref => {
  const m = (ref||'').match(/\/athletes\/(\d+)/);
  return m ? m[1] : null;
};

let _baseCache     = null;
let _statsCache    = null;
let _defStatsCache = null;
let _advStatsCache = null;

// ── loadBase ──────────────────────────────────────────────────────────────────
// Loads roster (name/pos/team) + stat leaders.
// Uses static snapshot if available, falls back to live ESPN API.
export async function loadBase() {
  if (_baseCache) return _baseCache;

  // Try static snapshot first
  const staticPlayers = await loadStaticJSON(STATIC_PLAYERS_URL);
  if (staticPlayers && Object.keys(staticPlayers).length > 100) {
    // Still fetch leaders for catMap (small, fast)
    const leadersRes = await axios.get(
      `https://sports.core.api.espn.com/v2/sports/football/leagues/nfl/seasons/${ESPN_SEASON}/types/${SEASON_TYPE}/leaders?limit=200`
    ).then(r => r.data).catch(() => ({ categories: [] }));

    const catMap = {};
    (leadersRes?.categories || []).forEach(cat => {
      catMap[cat.name] = {};
      (cat.leaders || []).forEach(entry => {
        const id = athIdFromRef(entry.athlete?.$ref);
        if (!id) return;
        catMap[cat.name][id] = { display: entry.displayValue ?? String(entry.value ?? '—'), num: parseFloat(entry.value) || 0 };
      });
    });

    _baseCache = { catMap, players: staticPlayers };
    return _baseCache;
  }

  // Fallback: live ESPN roster + leaders
  const [rosterResults, leadersRes] = await Promise.all([
    Promise.all(ALL_TEAM_IDS.map(id =>
      axios.get(`https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams/${id}/roster`)
        .then(r => r.data).catch(() => null)
    )),
    axios.get(
      `https://sports.core.api.espn.com/v2/sports/football/leagues/nfl/seasons/${ESPN_SEASON}/types/${SEASON_TYPE}/leaders?limit=200`
    ).then(r => r.data).catch(() => ({ categories: [] })),
  ]);

  const catMap = {};
  (leadersRes?.categories || []).forEach(cat => {
    catMap[cat.name] = {};
    (cat.leaders || []).forEach(entry => {
      const id = athIdFromRef(entry.athlete?.$ref);
      if (!id) return;
      catMap[cat.name][id] = { display: entry.displayValue ?? String(entry.value ?? '—'), num: parseFloat(entry.value) || 0 };
    });
  });

  const players = {};
  const incl = new Set(['offense', 'defense', 'specialTeam', 'injuredReserveOrOut']);
  rosterResults.filter(Boolean).forEach(data => {
    const team = data?.team?.abbreviation || '';
    (data?.athletes || []).forEach(grp => {
      if (!incl.has(grp.position)) return;
      (grp.items || []).forEach(p => {
        const posKey = POS_MAP[p.position?.abbreviation || ''];
        if (!posKey || !p.id) return;
        players[p.id] = {
          id: p.id,
          name: p.displayName || '—',
          shortName: p.shortName || p.displayName || '—',
          headshot: p.headshot?.href || null,
          team,
          posKey,
        };
      });
    });
  });

  _baseCache = { catMap, players };
  return _baseCache;
}

// ── loadPlayerStats ───────────────────────────────────────────────────────────
// Offensive stats. Uses static snapshot if available.
export async function loadPlayerStats(playerIds) {
  if (_statsCache) return _statsCache;

  const staticStats = await loadStaticJSON(STATIC_OFF_STATS_URL);
  if (staticStats && Object.keys(staticStats).length > 50) {
    _statsCache = staticStats;
    return _statsCache;
  }

  // Fallback: live ESPN per-player fetch
  const fetchWithRetry = (url, retries = 2) =>
    axios.get(url).catch(err => retries > 0 ? fetchWithRetry(url, retries - 1) : Promise.reject(err));

  const results = await Promise.all(
    playerIds.map(id =>
      fetchWithRetry(
        `https://sports.core.api.espn.com/v2/sports/football/leagues/nfl/seasons/${ESPN_SEASON}/types/${SEASON_TYPE}/athletes/${id}/statistics`
      ).then(r => ({ id, data: r.data })).catch(() => ({ id, data: null }))
    )
  );

  const statsMap = {};
  results.forEach(({ id, data }) => {
    if (!data) return;
    const categories = data?.splits?.categories || [];
    const cat = {};
    categories.forEach(c => {
      cat[c.name] = {};
      (c.stats || []).forEach(s => { cat[c.name][s.name] = s.value; });
    });
    statsMap[id] = {
      gamesPlayed:           cat.general?.gamesPlayed          ?? 0,
      fumblesLost:           cat.general?.fumblesLost          ?? 0,
      fumbles:               cat.general?.fumbles               ?? 0,
      passingYards:          cat.passing?.passingYards          ?? 0,
      passingTouchdowns:     cat.passing?.passingTouchdowns     ?? 0,
      interceptions:         cat.passing?.interceptions         ?? 0,
      passingAttempts:       cat.passing?.passingAttempts       ?? 0,
      completionPct:         cat.passing?.completionPct         ?? 0,
      interceptionPct:       cat.passing?.interceptionPct       ?? 0,
      sacks:                 cat.passing?.sacks                 ?? 0,
      QBRating:              cat.passing?.QBRating              ?? 0,
      rushingYards:          cat.rushing?.rushingYards          ?? 0,
      rushingTouchdowns:     cat.rushing?.rushingTouchdowns     ?? 0,
      rushingAttempts:       cat.rushing?.rushingAttempts       ?? 0,
      yardsPerRushAttempt:   cat.rushing?.yardsPerRushAttempt   ?? cat.rushing?.avgGain ?? 0,
      yardsPerReception:     cat.receiving?.yardsPerReception   ?? 0,
      receptions:            cat.receiving?.receptions          ?? 0,
      receivingYards:        cat.receiving?.receivingYards      ?? 0,
      receivingTouchdowns:   cat.receiving?.receivingTouchdowns ?? 0,
      receivingTargets:      cat.receiving?.receivingTargets    ?? 0,
      receivingFirstDowns:   cat.receiving?.receivingFirstDowns ?? 0,
    };
  });

  _statsCache = statsMap;
  return _statsCache;
}

// ── loadDefensiveStats ────────────────────────────────────────────────────────
// Defensive stats. Uses static snapshot if available.
export async function loadDefensiveStats(playerIds) {
  if (_defStatsCache) return _defStatsCache;

  const staticDefStats = await loadStaticJSON(STATIC_DEF_STATS_URL);
  if (staticDefStats && Object.keys(staticDefStats).length > 50) {
    _defStatsCache = staticDefStats;
    return _defStatsCache;
  }

  // Fallback: live ESPN per-player fetch
  const fetchWithRetry = (url, retries = 2) =>
    axios.get(url).catch(err => retries > 0 ? fetchWithRetry(url, retries - 1) : Promise.reject(err));

  const results = await Promise.all(
    playerIds.map(id =>
      fetchWithRetry(
        `https://sports.core.api.espn.com/v2/sports/football/leagues/nfl/seasons/${ESPN_SEASON}/types/${SEASON_TYPE}/athletes/${id}/statistics`
      ).then(r => ({ id, data: r.data })).catch(() => ({ id, data: null }))
    )
  );

  const statsMap = {};
  results.forEach(({ id, data }) => {
    if (!data) return;
    const categories = data?.splits?.categories || [];
    const cat = {};
    categories.forEach(c => {
      cat[c.name] = {};
      (c.stats || []).forEach(s => { cat[c.name][s.name] = s.value; });
    });
    statsMap[id] = {
      gamesPlayed:    cat.general?.gamesPlayed      ?? 0,
      sacks:          cat.defensive?.sacks           ?? 0,
      QBHits:         cat.defensive?.QBHits          ?? 0,
      tacklesForLoss: cat.defensive?.tacklesForLoss  ?? 0,
      totalTackles:   cat.defensive?.totalTackles    ?? 0,
      passesDefended: cat.defensive?.passesDefended  ?? 0,
      interceptions:  cat.defensiveInterceptions?.interceptions ?? cat.defensive?.interceptions ?? 0,
      soloTackles:    cat.defensive?.soloTackles     ?? 0,
      assistTackles:  cat.defensive?.assistTackles   ?? 0,
      forcedFumbles:  cat.general?.fumblesForced     ?? 0,
    };
  });

  _defStatsCache = statsMap;
  return _defStatsCache;
}

// ── loadAdvancedStats ─────────────────────────────────────────────────────────
// Loads nflverse advanced stats (NGS + PFR) joined to ESPN player IDs.
// Built by scripts/fetch-advanced-stats.js — keyed by ESPN player ID string.
const STATIC_ADV_STATS_URL = `/data/advanced_stats_2025.json`;

export async function loadAdvancedStats() {
  if (_advStatsCache) return _advStatsCache;
  try {
    const data = await loadStaticJSON(STATIC_ADV_STATS_URL);
    _advStatsCache = data || {};
    return _advStatsCache;
  } catch {
    return {};
  }
}
